(() => {
  'use strict';


  /* =======================================================
     FIND HALLOWEEN ROOT
     ======================================================= */

  const root =
    document.querySelector(
      '[data-bubs-halloween]'
    );

  if (!root) return;


  /* Prevent accidental double initialization */

  if (root.dataset.halloweenInitialized === 'true') {
    return;
  }

  root.dataset.halloweenInitialized =
    'true';


  /* =======================================================
     ELEMENTS
     ======================================================= */

  const dynamicLayer =
    root.querySelector(
      '[data-halloween-dynamic]'
    );

  const ghost =
    root.querySelector(
      '[data-halloween-ghost]'
    );

  const cartRoot =
    root.querySelector(
      '[data-halloween-cart-burst]'
    );


  /* =======================================================
     ASSET URLS
     ======================================================= */

  const batSrc =
    root.dataset.batSrc || '';

  const pumpkinSrc =
    root.dataset.pumpkinSrc || '';


  /* =======================================================
     DEVICE / ACCESSIBILITY
     ======================================================= */

  const reducedMotion =
    window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

  const coarsePointer =
    window.matchMedia(
      '(hover: none), (pointer: coarse)'
    ).matches;

  const mobileMedia =
    window.matchMedia(
      '(max-width: 749px)'
    );


  /* =======================================================
     SETTINGS
     ======================================================= */

  const settings = {

    bats:
      root.dataset.bats === 'true',

    batFrequency:
      root.dataset.batFrequency || 'low',

    pumpkins:
      root.dataset.pumpkins === 'true',

    ghost:
      root.dataset.ghost === 'true',

    sparkles:
      root.dataset.sparkles === 'true',

    cart:
      root.dataset.cartAnimation === 'true',

    intensity:
      root.dataset.intensity || 'subtle',

    mobile:
      root.dataset.mobileMode || 'minimal'

  };


  /* =======================================================
     HELPERS
     ======================================================= */

  function isMobile() {

    return mobileMedia.matches;

  }


  function random(min, max) {

    return (
      Math.random() * (max - min) + min
    );

  }


  function randomItem(array) {

    return array[
      Math.floor(
        Math.random() * array.length
      )
    ];

  }


  function remove(element) {

    if (
      element &&
      element.parentNode
    ) {

      element.parentNode.removeChild(
        element
      );

    }

  }


  function mobileDisabled() {

    return (
      isMobile() &&
      settings.mobile === 'off'
    );

  }



  /* =======================================================
     BAT FREQUENCY
     More active than previous version
     ======================================================= */

  function batFrequencyRange() {

    switch (settings.batFrequency) {

      case 'high':
        return [
          6000,
          11000
        ];

      case 'medium':
        return [
          10000,
          17000
        ];

      default:
        return [
          16000,
          26000
        ];

    }

  }



  /* =======================================================
     BAT FLIGHT ROUTES

     Multiple completely different entry/exit paths.
     ======================================================= */

  function getBatRoutes() {

    const w =
      window.innerWidth;

    const h =
      Math.min(
        window.innerHeight * 0.55,
        480
      );


    return [

      /* 1: Left → upper right */

      [
        [-100, h * 0.38],
        [w * 0.16, h * 0.15],
        [w * 0.38, h * 0.30],
        [w * 0.67, h * 0.12],
        [w + 100, h * 0.26]
      ],


      /* 2: Right → upper left */

      [
        [w + 100, h * 0.21],
        [w * 0.82, h * 0.38],
        [w * 0.58, h * 0.13],
        [w * 0.28, h * 0.31],
        [-100, h * 0.18]
      ],


      /* 3: Top-left diagonal */

      [
        [w * 0.05, -90],
        [w * 0.18, h * 0.16],
        [w * 0.41, h * 0.08],
        [w * 0.69, h * 0.32],
        [w + 100, h * 0.20]
      ],


      /* 4: Top-right diagonal */

      [
        [w * 0.94, -90],
        [w * 0.78, h * 0.17],
        [w * 0.57, h * 0.09],
        [w * 0.30, h * 0.31],
        [-100, h * 0.21]
      ],


      /* 5: Low left sweep */

      [
        [-100, h * 0.78],
        [w * 0.18, h * 0.47],
        [w * 0.44, h * 0.63],
        [w * 0.73, h * 0.38],
        [w + 100, h * 0.51]
      ],


      /* 6: Low right sweep */

      [
        [w + 100, h * 0.72],
        [w * 0.79, h * 0.40],
        [w * 0.53, h * 0.58],
        [w * 0.25, h * 0.35],
        [-100, h * 0.48]
      ],


      /* 7: Left looping wave */

      [
        [-100, h * 0.28],
        [w * 0.23, h * 0.40],
        [w * 0.43, h * 0.12],
        [w * 0.68, h * 0.40],
        [w + 100, h * 0.18]
      ],


      /* 8: Right looping wave */

      [
        [w + 100, h * 0.31],
        [w * 0.77, h * 0.13],
        [w * 0.52, h * 0.41],
        [w * 0.26, h * 0.17],
        [-100, h * 0.36]
      ]

    ];

  }



  /* =======================================================
     CREATE ONE BAT
     ======================================================= */

  function createBat() {

    if (
      !batSrc ||
      !dynamicLayer ||
      reducedMotion ||
      mobileDisabled()
    ) {

      return;

    }


    const bat =
      document.createElement('img');


    bat.className =
      'bubs-halloween-bat-gif';


    bat.src =
      batSrc;


    bat.alt =
      '';


    bat.draggable =
      false;


    bat.setAttribute(
      'aria-hidden',
      'true'
    );


    /* Random bat size */

    const size =
      isMobile()
        ? random(35, 50)
        : random(58, 88);


    bat.style.width =
      `${size}px`;


    dynamicLayer.appendChild(
      bat
    );


    /* Pick completely random route */

    const route =
      randomItem(
        getBatRoutes()
      );


    /*
      Add variation to every route,
      so the same route never looks identical.
    */

    const points =
      route.map(
        ([x, y]) => [

          x + random(-35, 35),

          y + random(-24, 24)

        ]
      );


    const movingRight =
      points[
        points.length - 1
      ][0] > points[0][0];


    const horizontalDirection =
      movingRight
        ? 1
        : -1;


    /*
      Keep rotation smoother instead of
      choosing huge random jumps.
    */

    const rotationA =
      random(-8, 8);

    const rotationB =
      random(-12, 12);

    const rotationC =
      random(-7, 7);


    const animation =
      bat.animate(

        [

          {
            offset: 0,

            transform:
              `translate3d(
                ${points[0][0]}px,
                ${points[0][1]}px,
                0
              )
              rotate(${rotationA}deg)
              scaleX(${horizontalDirection})
              scale(.9)`,

            opacity: 0
          },


          {
            offset: 0.10,

            transform:
              `translate3d(
                ${points[1][0]}px,
                ${points[1][1]}px,
                0
              )
              rotate(${rotationB}deg)
              scaleX(${horizontalDirection})
              scale(1)`,

            opacity:
              random(0.64, 0.84)
          },


          {
            offset: 0.35,

            transform:
              `translate3d(
                ${points[2][0]}px,
                ${points[2][1]}px,
                0
              )
              rotate(${rotationC}deg)
              scaleX(${horizontalDirection})
              scale(${random(.94, 1.06)})`,

            opacity:
              random(0.68, 0.88)
          },


          {
            offset: 0.70,

            transform:
              `translate3d(
                ${points[3][0]}px,
                ${points[3][1]}px,
                0
              )
              rotate(${rotationB * -0.6}deg)
              scaleX(${horizontalDirection})
              scale(${random(.95, 1.08)})`,

            opacity:
              random(0.63, 0.84)
          },


          {
            offset: 1,

            transform:
              `translate3d(
                ${points[4][0]}px,
                ${points[4][1]}px,
                0
              )
              rotate(${rotationA * -1}deg)
              scaleX(${horizontalDirection})
              scale(.92)`,

            opacity: 0
          }

        ],

        {

          duration:
            random(
              6200,
              9800
            ),

          easing:
            'cubic-bezier(.42,.05,.55,.95)',

          fill:
            'forwards'

        }

      );


    animation.finished
      .then(
        () => remove(bat)
      )
      .catch(
        () => remove(bat)
      );

  }



  /* =======================================================
     BAT GROUP
     ======================================================= */

  function batGroup() {

    let count = 2;


    if (
      settings.intensity === 'medium'
    ) {

      count = 3;

    }


    if (
      settings.intensity === 'full'
    ) {

      count = 4;

    }


    if (isMobile()) {

      count =
        settings.mobile === 'full'
          ? 2
          : 1;

    }


    for (
      let i = 0;
      i < count;
      i += 1
    ) {

      window.setTimeout(

        createBat,

        i * random(
          450,
          900
        )

      );

    }

  }



  /* =======================================================
     BAT SCHEDULER
     ======================================================= */

  function scheduleBats(
    initial = false
  ) {

    if (
      !settings.bats ||
      reducedMotion ||
      mobileDisabled() ||
      (
        isMobile() &&
        settings.mobile !== 'full'
      )
    ) {

      return;

    }


    const [min, max] =
      batFrequencyRange();


    const delay =
      initial
        ? random(
            2500,
            5000
          )
        : random(
            min,
            max
          );


    window.setTimeout(
      () => {

        batGroup();

        scheduleBats(
          false
        );

      },
      delay
    );

  }



  /* =======================================================
     RANDOM BOUNCING PUMPKIN
     Approx. 10.5–12 seconds on screen.
     ======================================================= */

  function showPumpkin() {

    if (
      !settings.pumpkins ||
      !pumpkinSrc ||
      reducedMotion ||
      mobileDisabled()
    ) {

      return;

    }


    const pumpkin =
      document.createElement(
        'img'
      );


    pumpkin.className =
      'bubs-halloween-popup-pumpkin';


    pumpkin.src =
      pumpkinSrc;


    pumpkin.alt =
      '';


    pumpkin.draggable =
      false;


    pumpkin.setAttribute(
      'aria-hidden',
      'true'
    );


    /*
      Keep away from extreme left/right edges.
    */

    const x =
      isMobile()
        ? random(12, 76)
        : random(10, 82);


    pumpkin.style.left =
      `${x}vw`;


    /*
      Larger desktop pumpkin.
    */

    const size =
      isMobile()
        ? random(72, 92)
        : random(120, 155);


    pumpkin.style.width =
      `${size}px`;


    root.appendChild(
      pumpkin
    );


    /*
      Entire sequence lasts around 10–12 seconds.
    */

    const duration =
      random(
        10500,
        12000
      );


    const animation =
      pumpkin.animate(

        [

          /* hidden below viewport */

          {
            offset: 0,

            transform:
              'translateY(125%) scale(.78)',

            opacity: 0
          },


          /* pop into screen */

          {
            offset: .07,

            transform:
              'translateY(0) scale(1)',

            opacity: .96
          },


          /* first large bounce */

          {
            offset: .16,

            transform:
              'translateY(-58px) rotate(-5deg) scale(1.03)',

            opacity: 1
          },


          {
            offset: .24,

            transform:
              'translateY(0) rotate(4deg) scale(1)',

            opacity: 1
          },


          /* second bounce */

          {
            offset: .33,

            transform:
              'translateY(-42px) rotate(-4deg)',

            opacity: 1
          },


          {
            offset: .41,

            transform:
              'translateY(0) rotate(3deg)',

            opacity: 1
          },


          /* third bounce */

          {
            offset: .50,

            transform:
              'translateY(-30px) rotate(-3deg)',

            opacity: 1
          },


          {
            offset: .58,

            transform:
              'translateY(0) rotate(2deg)',

            opacity: 1
          },


          /* continue playful bouncing */

          {
            offset: .67,

            transform:
              'translateY(-22px) rotate(-2deg)',

            opacity: 1
          },


          {
            offset: .74,

            transform:
              'translateY(0) rotate(2deg)',

            opacity: 1
          },


          {
            offset: .81,

            transform:
              'translateY(-15px) rotate(-1deg)',

            opacity: 1
          },


          {
            offset: .87,

            transform:
              'translateY(0) rotate(1deg)',

            opacity: 1
          },


          /* stay visible */

          {
            offset: .93,

            transform:
              'translateY(-7px) rotate(-1deg)',

            opacity: 1
          },


          {
            offset: .96,

            transform:
              'translateY(0) scale(1)',

            opacity: .95
          },


          /* leave below viewport */

          {
            offset: 1,

            transform:
              'translateY(125%) scale(.9)',

            opacity: 0
          }

        ],

        {

          duration,

          easing:
            'cubic-bezier(.22,.75,.30,1)',

          fill:
            'forwards'

        }

      );


    animation.finished
      .then(
        () => remove(pumpkin)
      )
      .catch(
        () => remove(pumpkin)
      );

  }



  /* =======================================================
     PUMPKIN SCHEDULER
     ======================================================= */

  function schedulePumpkin(
    initial = false
  ) {

    if (
      !settings.pumpkins ||
      reducedMotion ||
      mobileDisabled()
    ) {

      return;

    }


    const delay =
      initial
        ? random(
            9000,
            16000
          )
        : random(
            25000,
            42000
          );


    window.setTimeout(
      () => {

        showPumpkin();

        schedulePumpkin(
          false
        );

      },
      delay
    );

  }



  /* =======================================================
     FRIENDLY GHOST
     ======================================================= */

  function showGhost() {

    if (
      !ghost ||
      reducedMotion ||
      mobileDisabled()
    ) {

      return;

    }


    /*
      Random vertical position every visit.
    */

    ghost.style.top =
      `${random(34, 70)}vh`;


    ghost.classList.remove(
      'is-reacting',
      'is-peeking'
    );


    /*
      Trigger reflow so animation can restart.
    */

    void ghost.offsetWidth;


    ghost.classList.add(
      'is-peeking'
    );


    window.setTimeout(
      () => {

        if (
          !ghost.classList.contains(
            'is-reacting'
          )
        ) {

          ghost.classList.remove(
            'is-peeking'
          );

        }

      },
      6400
    );

  }



  /* =======================================================
     GHOST SCHEDULER
     ======================================================= */

  function scheduleGhost(
    initial = false
  ) {

    if (
      !settings.ghost ||
      reducedMotion ||
      mobileDisabled() ||
      (
        isMobile() &&
        settings.mobile === 'minimal'
      )
    ) {

      return;

    }


    const delay =
      initial
        ? random(
            15000,
            23000
          )
        : random(
            27000,
            43000
          );


    window.setTimeout(
      () => {

        showGhost();

        scheduleGhost(
          false
        );

      },
      delay
    );

  }



  /* =======================================================
     GHOST CLICK SOUND

     Sound happens only after a user click,
     respecting browser autoplay restrictions.
     ======================================================= */

  function playGhostSound() {

    const AudioContextClass =
      window.AudioContext ||
      window.webkitAudioContext;


    if (!AudioContextClass) {

      return;

    }


    const context =
      new AudioContextClass();


    const master =
      context.createGain();


    master.connect(
      context.destination
    );


    const start =
      context.currentTime;


    master.gain.setValueAtTime(
      0.0001,
      start
    );


    master.gain.exponentialRampToValueAtTime(
      0.075,
      start + 0.025
    );


    master.gain.exponentialRampToValueAtTime(
      0.0001,
      start + 0.8
    );


    const notes = [

      {
        frequency: 520,
        time: 0
      },

      {
        frequency: 670,
        time: 0.12
      },

      {
        frequency: 840,
        time: 0.24
      },

      {
        frequency: 700,
        time: 0.40
      }

    ];


    notes.forEach(
      (note) => {

        const oscillator =
          context.createOscillator();


        const noteGain =
          context.createGain();


        oscillator.type =
          'sine';


        oscillator.frequency.value =
          note.frequency;


        oscillator.connect(
          noteGain
        );


        noteGain.connect(
          master
        );


        const noteStart =
          start + note.time;


        noteGain.gain.setValueAtTime(
          0.0001,
          noteStart
        );


        noteGain.gain.exponentialRampToValueAtTime(
          0.5,
          noteStart + 0.02
        );


        noteGain.gain.exponentialRampToValueAtTime(
          0.0001,
          noteStart + 0.18
        );


        oscillator.start(
          noteStart
        );


        oscillator.stop(
          noteStart + 0.2
        );

      }
    );


    window.setTimeout(
      () => {

        context
          .close()
          .catch(
            () => {}
          );

      },
      1200
    );

  }



  /* =======================================================
     GHOST CLICK PARTICLES
     ======================================================= */

  function ghostReactionParticles() {

    if (!ghost) return;


    const rect =
      ghost.getBoundingClientRect();


    const symbols = [
      '💜',
      '✨',
      '🧡',
      '⭐'
    ];


    for (
      let i = 0;
      i < 6;
      i += 1
    ) {

      const particle =
        document.createElement(
          'span'
        );


      particle.className =
        'bubs-ghost-reaction-particle';


      particle.textContent =
        randomItem(
          symbols
        );


      particle.style.left =
        `${
          rect.left +
          rect.width / 2
        }px`;


      particle.style.top =
        `${
          rect.top +
          rect.height / 2
        }px`;


      document.body.appendChild(
        particle
      );


      const x =
        random(
          -60,
          40
        );


      const y =
        random(
          -70,
          -20
        );


      const animation =
        particle.animate(

          [

            {
              transform:
                'translate3d(0,0,0) scale(.35)',

              opacity: 0
            },


            {
              offset: .18,

              opacity: 1
            },


            {
              transform:
                `translate3d(
                  ${x}px,
                  ${y}px,
                  0
                )
                scale(1.1)
                rotate(
                  ${random(-25, 25)}deg
                )`,

              opacity: 0
            }

          ],

          {

            duration:
              random(
                850,
                1300
              ),

            easing:
              'ease-out',

            fill:
              'forwards'

          }

        );


      animation.finished
        .then(
          () => remove(particle)
        )
        .catch(
          () => remove(particle)
        );

    }

  }



  /* =======================================================
     GHOST CLICK
     ======================================================= */

  if (ghost) {

    ghost.addEventListener(
      'click',
      (event) => {

        event.preventDefault();

        event.stopPropagation();


        playGhostSound();


        ghost.classList.remove(
          'is-peeking'
        );


        ghost.classList.add(
          'is-reacting'
        );


        ghostReactionParticles();


        window.setTimeout(
          () => {

            ghost.classList.remove(
              'is-reacting'
            );

          },
          1650
        );

      }
    );

  }



  /* =======================================================
     CURSOR SPARKLES
     ======================================================= */

  let sparkleCooldown =
    false;


  function createSparkle(
    x,
    y
  ) {

    const sparkle =
      document.createElement(
        'span'
      );


    sparkle.className =
      'bubs-halloween-sparkle';


    sparkle.style.left =
      `${x}px`;


    sparkle.style.top =
      `${y}px`;


    sparkle.style.color =
      Math.random() > 0.5
        ? 'var(--hw-orange)'
        : 'var(--hw-purple)';


    root.appendChild(
      sparkle
    );


    const moveX =
      random(
        -12,
        12
      );


    const moveY =
      random(
        -25,
        -10
      );


    const animation =
      sparkle.animate(

        [

          {
            transform:
              'translate(-50%, -50%) scale(.3)',

            opacity: 0
          },


          {
            offset: .18,

            opacity: .82
          },


          {
            transform:
              `translate(
                calc(-50% + ${moveX}px),
                calc(-50% + ${moveY}px)
              )
              scale(1)`,

            opacity: 0
          }

        ],

        {

          duration: 700,

          easing:
            'ease-out',

          fill:
            'forwards'

        }

      );


    animation.finished
      .then(
        () => remove(sparkle)
      )
      .catch(
        () => remove(sparkle)
      );

  }



  /* Enable desktop sparkles */

  if (
    settings.sparkles &&
    !coarsePointer &&
    !isMobile() &&
    !reducedMotion
  ) {

    document.addEventListener(

      'pointermove',

      (event) => {

        if (
          sparkleCooldown
        ) {

          return;

        }


        sparkleCooldown =
          true;


        window.setTimeout(
          () => {

            sparkleCooldown =
              false;

          },
          85
        );


        if (
          Math.random() < 0.38
        ) {

          createSparkle(
            event.clientX,
            event.clientY
          );

        }

      },

      {
        passive: true
      }

    );

  }



  /* =======================================================
     CART CELEBRATION
     ======================================================= */

  let lastCartAnimation =
    0;


  function cartBurst() {

    if (
      !settings.cart ||
      !cartRoot ||
      reducedMotion ||
      mobileDisabled()
    ) {

      return;

    }


    const now =
      Date.now();


    if (
      now -
      lastCartAnimation <
      900
    ) {

      return;

    }


    lastCartAnimation =
      now;


    /* Pumpkin */

    const pumpkin =
      document.createElement(
        'span'
      );


    pumpkin.className =
      'bubs-halloween-mini-pumpkin';


    pumpkin.textContent =
      '🎃';


    cartRoot.appendChild(
      pumpkin
    );


    const pumpkinAnimation =
      pumpkin.animate(

        [

          {
            transform:
              'translate3d(0,0,0) scale(.3)',

            opacity: 0
          },


          {
            offset: .22,

            opacity: 1
          },


          {
            transform:
              'translate3d(-8px,-30px,0) scale(1.12)',

            opacity: 0
          }

        ],

        {

          duration: 1200,

          easing:
            'ease-out',

          fill:
            'forwards'

        }

      );


    pumpkinAnimation.finished
      .then(
        () => remove(pumpkin)
      )
      .catch(
        () => remove(pumpkin)
      );


    /* Confetti */

    const count =
      settings.intensity === 'full'
        ? 10
        : settings.intensity === 'medium'
          ? 8
          : 6;


    for (
      let i = 0;
      i < count;
      i += 1
    ) {

      const particle =
        document.createElement(
          'span'
        );


      particle.className =
        'bubs-halloween-confetti';


      particle.style.background =
        i % 2
          ? 'var(--hw-orange)'
          : 'var(--hw-purple)';


      cartRoot.appendChild(
        particle
      );


      const angle =
        (
          Math.PI *
          2 *
          i
        ) /
        count;


      const distance =
        random(
          24,
          48
        );


      const x =
        Math.cos(angle) *
        distance;


      const y =
        Math.sin(angle) *
        distance;


      const animation =
        particle.animate(

          [

            {
              transform:
                'translate3d(0,0,0) rotate(0deg)',

              opacity: 1
            },


            {
              transform:
                `translate3d(
                  ${x}px,
                  ${y}px,
                  0
                )
                rotate(
                  ${random(100, 280)}deg
                )`,

              opacity: 0
            }

          ],

          {

            duration:
              random(
                900,
                1450
              ),

            easing:
              'ease-out',

            fill:
              'forwards'

          }

        );


      animation.finished
        .then(
          () => remove(particle)
        )
        .catch(
          () => remove(particle)
        );

    }

  }



  /* =======================================================
     WATCH AJAX ADD-TO-CART
     Does NOT modify Shopify cart functionality.
     ======================================================= */

  function installCartWatcher() {

    if (
      !settings.cart ||
      window.__bubsHalloweenCartWatcher
    ) {

      return;

    }


    window.__bubsHalloweenCartWatcher =
      true;


    const originalFetch =
      window.fetch.bind(
        window
      );


    window.fetch =
      async (...args) => {

        const response =
          await originalFetch(
            ...args
          );


        try {

          let url = '';


          const request =
            args[0];


          if (
            typeof request === 'string'
          ) {

            url =
              request;

          } else if (
            request &&
            typeof request.url === 'string'
          ) {

            url =
              request.url;

          }


          if (
            response.ok &&
            /\/cart\/add(?:\.js)?(?:\?|$)/.test(
              url
            )
          ) {

            window.setTimeout(
              cartBurst,
              120
            );

          }

        } catch (error) {

          /*
            Decorative feature only.
            Never interfere with cart.
          */

        }


        return response;

      };

  }



  /* =======================================================
     START HALLOWEEN EXPERIENCE
     ======================================================= */

  if (
    !mobileDisabled()
  ) {

    scheduleBats(
      true
    );


    schedulePumpkin(
      true
    );


    scheduleGhost(
      true
    );

  }


  installCartWatcher();

})();